import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { ActionLogsDataType } from '../types/actionLogsTypes';
import { ActionLogsTableFilterDataType } from '../types/filterTypes';
import { useActionLogsPageQueryParams } from './useActionLogsPageQueryParams';

const URL_SEARCH_KEY = 'search';
const URL_FILTER_PREFIX = 'filter';
const QUERY_DEBOUNCE_MS = 300;

export const DEFAULT_ACTION_LOGS_TABLE_FILTER_STATE: ActionLogsTableFilterDataType = {
  device_ip: '',
  device_vendor: '',
  region: '',
  user_action: '',
  user_email: '',
  building_code: '',
};

const FILTER_KEYS = Object.keys(
  DEFAULT_ACTION_LOGS_TABLE_FILTER_STATE
) as (keyof ActionLogsTableFilterDataType)[];

const SEARCHABLE_FIELDS: (keyof ActionLogsTableFilterDataType)[] = [
  'device_ip',
  'device_vendor',
  'region',
  'user_action',
  'user_email',
  'building_code',
];

function filterUrlKey(key: keyof ActionLogsTableFilterDataType): string {
  return `${URL_FILTER_PREFIX}[${key}]`;
}

function readFilterStateFromUrl(params: URLSearchParams): ActionLogsTableFilterDataType {
  const state = { ...DEFAULT_ACTION_LOGS_TABLE_FILTER_STATE };
  FILTER_KEYS.forEach((key) => {
    const value = params.get(filterUrlKey(key));
    if (value) {
      state[key] = value;
    }
  });
  return state;
}

function readSearchQueryFromUrl(params: URLSearchParams): string {
  return params.get(URL_SEARCH_KEY) ?? '';
}

function filterStatesAreEqual(
  a: ActionLogsTableFilterDataType,
  b: ActionLogsTableFilterDataType
): boolean {
  return FILTER_KEYS.every((key) => a[key] === b[key]);
}

function matchesField(
  obj: ActionLogsDataType,
  key: keyof ActionLogsTableFilterDataType,
  filterValue: string
): boolean {
  const trimmed = filterValue.trim();
  if (trimmed === '') {
    return true;
  }
  const value = obj[key];
  if (value == null) {
    return false;
  }
  return value.toLowerCase().includes(trimmed.toLowerCase());
}

export function useActionLogsQuery(allActionLogs: ActionLogsDataType[]) {
  // Shared with the date-range filter so all URL writers merge via one setParams helper.
  const { searchParams, setParams } = useActionLogsPageQueryParams();

  const [searchQuery, setSearchQuery] = useState<string>(() =>
    readSearchQueryFromUrl(searchParams)
  );
  const [filterState, setFilterState] = useState<ActionLogsTableFilterDataType>(() =>
    readFilterStateFromUrl(searchParams)
  );

  // Debounced snapshot so filtering and URL sync don't re-run on every keystroke.
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  const [debouncedFilterState, setDebouncedFilterState] = useState(filterState);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setDebouncedFilterState(filterState);
    }, QUERY_DEBOUNCE_MS);

    return () => window.clearTimeout(handle);
  }, [searchQuery, filterState]);

  // Sync debounced state -> URL via setParams, merging so unrelated params
  // (range/start/end) survive.
  useEffect(() => {
    const trimmedSearch = debouncedSearchQuery.trim();
    const nextParams: Record<string, string | undefined> = {
      [URL_SEARCH_KEY]: trimmedSearch || undefined,
    };
    FILTER_KEYS.forEach((key) => {
      const value = debouncedFilterState[key].trim();
      nextParams[filterUrlKey(key)] = value || undefined;
    });
    setParams(nextParams);
  }, [debouncedSearchQuery, debouncedFilterState, setParams]);

  // Sync URL -> state, so browser back/forward and external link edits are
  // reflected. Skipped on first mount since state is already seeded from the URL.
  const skipNextUrlSync = useRef(true);
  useEffect(() => {
    if (skipNextUrlSync.current) {
      skipNextUrlSync.current = false;
      return;
    }

    const nextSearchQuery = readSearchQueryFromUrl(searchParams);
    const nextFilterState = readFilterStateFromUrl(searchParams);

    if (nextSearchQuery !== searchQuery) {
      setSearchQuery(nextSearchQuery);
      setDebouncedSearchQuery(nextSearchQuery);
    }
    if (!filterStatesAreEqual(nextFilterState, filterState)) {
      setFilterState(nextFilterState);
      setDebouncedFilterState(nextFilterState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const filteredLogs = useMemo(() => {
    // Apply per-field filters (AND logic).
    let result = allActionLogs.filter(
      (obj) =>
        matchesField(obj, 'device_ip', debouncedFilterState.device_ip) &&
        matchesField(obj, 'device_vendor', debouncedFilterState.device_vendor) &&
        matchesField(obj, 'region', debouncedFilterState.region) &&
        matchesField(obj, 'user_action', debouncedFilterState.user_action) &&
        matchesField(obj, 'user_email', debouncedFilterState.user_email) &&
        matchesField(obj, 'building_code', debouncedFilterState.building_code)
    );

    // Apply global search on top of the filtered result.
    const q = debouncedSearchQuery.trim().toLowerCase();
    if (q) {
      result = result.filter((obj) =>
        SEARCHABLE_FIELDS.some((field) => {
          const value = obj[field];
          return typeof value === 'string' && value.toLowerCase().includes(q);
        })
      );
    }

    return result;
  }, [allActionLogs, debouncedSearchQuery, debouncedFilterState]);

  const setFilterValue = useCallback((key: keyof ActionLogsTableFilterDataType, value: string) => {
    setFilterState((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Clears apply immediately, skipping the debounce (discrete actions, not keystrokes).
  const clearFilter = useCallback(
    (key: keyof ActionLogsTableFilterDataType) => {
      const next = { ...filterState, [key]: '' };
      setFilterState(next);
      setDebouncedFilterState(next);
    },
    [filterState]
  );

  const resetFilters = useCallback(() => {
    const next = { ...DEFAULT_ACTION_LOGS_TABLE_FILTER_STATE };
    setFilterState(next);
    setDebouncedFilterState(next);
  }, []);

  const clearAll = useCallback(() => {
    resetFilters();
    setSearchQuery('');
    setDebouncedSearchQuery('');
  }, [resetFilters]);

  const activeFilterCount = useMemo(
    () => Object.values(filterState).filter((v) => v.trim() !== '').length,
    [filterState]
  );

  return {
    filteredLogs,
    searchQuery,
    setSearchQuery,
    filterState,
    setFilterValue,
    clearFilter,
    clearAll,
    activeFilterCount,
  };
}
